import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isAuthError } from "../../../shared";
import { registerDevState } from "../../../core";
import { notificationPreferencesService } from "../services";
import { NotificationPreferencesContext } from "./NotificationPreferencesContext";
import { useUser } from "../hooks/useUser";

// The single owner of the notification-preference state. Everything that reads
// or changes it goes through hooks/useNotificationPreferences.js, which reaches
// the same context object this file fills in.
//
// The state used to be an anonymous `subscribe_to_news` field on the user object,
// read and written only by a handler buried inside the profile page. Nothing else
// could reach it without knowing both the field name and the endpoint's quirks.
// It now has one named address: useNotificationPreferences().
//
// `useUser` is imported from its file rather than from `../hooks` on purpose: the
// hooks barrel also carries useNotificationPreferences, which imports the context
// object, and routing the provider through it would tie the two directions
// together for no reason.
export const NotificationPreferencesProvider = ({ children }) => {
  const { user, updateUser, refreshUser, clearAuth } = useUser();

  const [newsSubscription, setNewsSubscription] = useState(
    !!user?.subscribe_to_news,
  );
  const [toggling, setToggling] = useState(false);

  // Guards the request, not the render. State updates are async, so a second
  // click can land before `toggling` has flipped - only a ref is read soon enough.
  const togglingRef = useRef(false);

  // Re-seed whenever the server-provided user object reports a different value:
  // bootstrap, refreshUser, or a fresh login. Keyed on the value rather than the
  // object, so it fires only on a real change and never stomps an in-flight
  // optimistic toggle.
  useEffect(() => {
    setNewsSubscription(!!user?.subscribe_to_news);
  }, [user?.subscribe_to_news]);

  // Reassigned every render so the dev-console getter below always reads current
  // values without the effect having to re-run.
  const snapshotRef = useRef();
  snapshotRef.current = { newsSubscription, toggling };

  // See core/docs/testing.md - `__funooni.notificationPreferences()`.
  // Registered once on mount; a no-op outside development.
  useEffect(
    () =>
      registerDevState(
        "notificationPreferences",
        () => snapshotRef.current,
      ),
    [],
  );

  const toggleNewsSubscription = useCallback(async () => {
    // The endpoint takes no parameters - it flips whatever value the server
    // currently holds. Two overlapping requests therefore cancel each other out
    // and leave the UI showing the opposite of what is stored.
    if (togglingRef.current) return;
    togglingRef.current = true;
    setToggling(true);

    const previous = newsSubscription;
    setNewsSubscription(!previous);

    try {
      const { newsSubscription: confirmed } =
        await notificationPreferencesService.toggleNewsSubscription();

      if (confirmed === null) {
        // Unrecognised wording - re-read the user rather than leaving the
        // toggle on an unverified guess. The sync effect above picks the
        // fresh value up.
        await refreshUser();
      } else {
        setNewsSubscription(confirmed);
        // Mirror it back onto the user object so the sections that still read
        // `user.subscribe_to_news` directly stay truthful. Writing the same
        // value re-runs the sync effect harmlessly.
        updateUser({ subscribe_to_news: confirmed });
      }
    } catch (error) {
      console.error(error);
      setNewsSubscription(previous);

      if (isAuthError(error)) {
        toast.error("Session expired — please log in again.");
        clearAuth();
      } else {
        toast.error("Couldn't update subscription.");
      }
    } finally {
      togglingRef.current = false;
      setToggling(false);
    }
  }, [newsSubscription, updateUser, refreshUser, clearAuth]);

  return (
    <NotificationPreferencesContext.Provider
      value={{ newsSubscription, toggling, toggleNewsSubscription }}
    >
      {children}
    </NotificationPreferencesContext.Provider>
  );
};
